import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';

interface CapabilitiesFieldProps {
  /** Current capability slugs. */
  value: string[];
  /** Called with the next list whenever a slug is added or removed. */
  onChange: (next: string[]) => void;
  disabled?: boolean;
  testIdPrefix?: string;
}

/**
 * Controlled chip editor for free-form capability slugs. Mirrors the
 * persisted editor on `AdminOrganizationDetailPage` but is fully
 * controlled (value / onChange), so callers that batch the value into a
 * larger submit (e.g. the create-organization dialog) can drive it from
 * local state. Slugs are trimmed + lower-cased + de-duped on add, matching
 * how the server normalizes them, so what you see is what gets persisted.
 */
export function CapabilitiesField({
  value,
  onChange,
  disabled,
  testIdPrefix = 'capabilities',
}: CapabilitiesFieldProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function tryAdd(): void {
    const candidate = draft.trim().toLowerCase();
    if (candidate.length === 0) return;
    if (value.includes(candidate)) {
      setError(`"${candidate}" is already added`);
      return;
    }
    onChange([...value, candidate]);
    setDraft('');
    setError(null);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAdd();
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((slug) => (
            <Badge
              key={slug}
              variant="outline"
              className="gap-1.5 bg-muted/40 py-1 pl-2.5 pr-1 leading-none"
              data-testid={`${testIdPrefix}-chip-${slug}`}
            >
              <span className="font-mono">{slug}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => onChange(value.filter((c) => c !== slug))}
                aria-label={`Remove capability ${slug}`}
                className="-mr-0.5 rounded-full"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. runloop-plugins"
            aria-label="Add capability"
            aria-invalid={error ? true : undefined}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            data-testid={`${testIdPrefix}-input`}
          />
          {error && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={tryAdd}
          disabled={draft.trim().length === 0 || disabled}
          data-testid={`${testIdPrefix}-add`}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
